import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Btn } from '../components/Btn.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { Field, TextInput } from '../components/Field.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { useBranding } from '../lib/branding.tsx';
import { PoweredByFooter } from '../components/PoweredByFooter.tsx';
import { getAdminSupabase } from '../lib/supabase.ts';

export function ForgotPassword() {
  const { user } = useAdminAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [sent, setSent] = useState(false);
  const brandName = branding.companyName?.replace(/ Safety.*$/i, '') ?? 'Locate2u';

  useEffect(() => {
    if (user) navigate('/admin', { replace: true });
  }, [user, navigate]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const supabase = getAdminSupabase();
      const redirectTo = `${window.location.origin}/admin/reset-password`;
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo,
      });
      if (resetErr) throw resetErr;
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 text-center">
          {branding.logoUrl && (
            <img src={branding.logoUrl} alt={brandName} className="mx-auto mb-3 h-10 w-auto" />
          )}
          <div
            className="brand-wordmark mb-1 text-2xl"
            style={{ color: 'var(--brand-primary)' }}
            aria-label="Safety & Compliance"
          >
            Safety<span className="accent">&amp;</span>Compliance
          </div>
          <PoweredByFooter brandName={brandName} />
          <p className="mt-4 text-sm font-medium text-slate-700">Forgot your password?</p>
          <p className="mt-1 text-sm text-slate-500">
            {sent
              ? 'Check your inbox for a reset link.'
              : 'Enter your email and we’ll send a reset link.'}
          </p>
        </div>

        {sent ? (
          <div className="space-y-4 text-center">
            <p className="text-sm text-slate-600">
              If an admin account exists for <span className="font-medium">{email.trim()}</span>,
              we’ve emailed a link to reset your password. Check your inbox (and spam folder).
            </p>
            <Link
              to="/admin/login"
              className="inline-block text-xs text-slate-500 underline hover:text-slate-700"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={onSubmit}>
            <Field label="Email" required>
              <TextInput
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
            <ErrorMessage error={error} />
            <Btn type="submit" loading={submitting} disabled={!email.trim()} className="w-full">
              Send reset link
            </Btn>
            <div className="text-center text-xs">
              <Link to="/admin/login" className="text-slate-500 underline hover:text-slate-700">
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
